import 'dayjs/locale/es';
import 'dayjs/locale/en';
import { TextField, TextFieldProps } from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { Controller, ControllerProps } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';
import type { CSSProperties } from 'react';

/**
 * Componente DateTimePickerTranslated
 *
 * Selector de fecha y hora con soporte multilenguaje y validaciones.
 *
 * @component
 * @param {object} props - Props del componente.
 * @param {string} props.name - Nombre del campo en el formulario.
 * @param {any} props.control - Control de react-hook-form.
 * @param {object} [props.inputProps] - Props para el input.
 * @param {string} [props.size] - Tamaño del input.
 * @param {string} [props.variant] - Variante del input.
 * @param {boolean} [props.fullWidth] - Si el input ocupa todo el ancho.
 * @param {object} [props.validation] - Reglas de validación.
 * @param {string} props.type - Tipo de selector ('datetime-local', 'date', 'time').
 * @param {boolean} [props.disabled] - Si el campo está deshabilitado.
 * @param {Dayjs} [props.valueInField] - Valor inicial.
 * @param {function} [props.shouldDisableDate] - Función para deshabilitar fechas.
 * @param {object} [props.sx] - Estilos personalizados.
 * @param {boolean} [props.required] - Si el campo es requerido.
 * @example
 * <DateTimePickerTranslated name="fecha" control={control} type="datetime-local" />
 */

type DatePickerTranslatedProps<T> = {
  name: string;
  control: any;
  inputProps?: TextFieldProps['inputProps'];
  size?: TextFieldProps['size'];
  variant?: TextFieldProps['variant'];
  fullWidth?: boolean;
  validation?: ControllerProps['rules'];
  type: 'datetime-local' | 'date' | 'time';
  disabled?: boolean;
  valueInField?: Dayjs;
  shouldDisableDate?: (day: Dayjs) => boolean;
  sx?: CSSProperties;
  required?: boolean;
  minDate?: Date;
  maxDate?: Date;
  inputFormat?: string;
};

function DateTimePickerTranslated<T>({
  name,
  control,
  inputProps,
  size = 'medium',
  variant = 'standard',
  fullWidth = false,
  validation = {},
  disabled = false,
  valueInField,
  shouldDisableDate = () => false,
  required = false,
  inputFormat = null,
  minDate = null,
  maxDate = null,
  ...rest
}: DatePickerTranslatedProps<T>) {
  const { i18n, t } = useTranslation('global');
  const currentLang = i18n.language;

  return (
    <LocalizationProvider
      adapterLocale={currentLang}
      dateAdapter={AdapterDayjs}
    >
      <Controller
        control={control}
        disabled={disabled}
        name={name}
        render={({
          field: { value, onChange, onBlur },
          fieldState: { invalid, error }
        }) => (
          <DateTimePicker
            disableOpenPicker={disabled}
            inputFormat={
              currentLang === 'es' ? 'DD/MM/YYYY HH:mm' : 'MM/DD/YYYY HH:mm'
            }
            onChange={onChange}
            renderInput={(params) => (
              <TextField
                {...params}
                disabled={disabled}
                error={invalid}
                fullWidth={fullWidth}
                helperText={error ? error.message : params.helperText}
                inputProps={{
                  ...params.inputProps,
                  ...inputProps,
                  // readOnly: { disabled },
                  placeholder:
                    currentLang === 'es'
                      ? 'dd/mm/aaaa hh:mm'
                      : 'dd/mm/yyyy hh:mm'
                }}
                onBlur={onBlur}
                size={size}
                variant={variant}
                {...rest}
              />
            )}
            shouldDisableDate={shouldDisableDate}
            value={value || valueInField || null}
          />
        )}
        rules={{
          required: required ? t('common.commonMessages.required') : false,
          validate: (val) => {
            if (val) {
              const dateVal = dayjs(val);
              if (!dateVal.isValid()) {
                return t('common.commonMessages.invalidFormat'); // Formato inválido
              }

              if (minDate && dateVal.isBefore(dayjs(minDate), 'day')) {
                return t('common.commonMessages.minDate', {
                  date: dayjs(minDate).format(inputFormat || 'DD/MM/YYYY')
                }); // Fecha menor al mínimo permitido
              }

              if (maxDate && dateVal.isAfter(dayjs(maxDate), 'day')) {
                return t('common.commonMessages.maxDate', {
                  date: dayjs(maxDate).format(inputFormat || 'DD/MM/YYYY')
                }); // Fecha mayor al máximo permitido
              }

              return true;
            }

            // Validar si es requerido
            if (validation?.required || required) {
              return t('common.commonMessages.required');
            }

            return true;
          },
          ...validation
        }}
      />
    </LocalizationProvider>
  );
}

export default DateTimePickerTranslated;
